import React, { useState, useEffect, FC, ReactNode } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
// ADICIONADO: serverTimestamp para o campo createdAt
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'; 
import { auth, db, googleProvider } from '../firebase'; 
import { useAuth, UserRole } from "./AuthContext";
import { TherapistStatus } from '../types/therapist';
import { ensureTherapistWelcomeTrial } from '../services/welcomeTrial';
import { ensureInfluencerSignupBonus } from '../services/influencerSignupBonus';
import { signupInfluencerCreateFields } from '../utils/influencerAttribution';
import { MARKETING_HOME_URL, POST_LOGIN_ROUTE } from '../constants/externalLinks';
import { User, Mail, Lock, Phone } from 'lucide-react';
import { formatBrazilPhone, isValidBrazilPhone } from '../utils/phoneFormat';
import Swal from 'sweetalert2';

// Tipos de dados
type FormMode = "login" | "register";
type RegisterRole = "paciente" | "terapeuta";

interface LoginFormProps {
  embedded?: boolean;
  title?: string;
  description?: string;
}

interface FormInputProps {
  id: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  required: boolean;
  children: ReactNode;
}

const FormInput: FC<FormInputProps> = ({ id, type, value, onChange, placeholder, required, children }) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-sm font-medium text-gray-600 mb-2">{placeholder}</label>
    <div className="relative">
      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
        {children}
      </span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder=""
        required={required}
        className="w-full pl-10 pr-4 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-[#2B5A41] focus:bg-white outline-none transition text-gray-700"
      />
    </div>
  </div>
);

const GOOGLE_REDIRECT_FLAG = "constela_google_auth_redirect";

async function ensureGoogleUserDocument(user: {
  uid: string;
  displayName: string | null;
  email: string | null;
}) {
  const userDocRef = doc(db, "users", user.uid);
  const userDoc = await getDoc(userDocRef);

  if (!userDoc.exists()) {
    const newUser = {
      uid: user.uid,
      name: user.displayName || "Novo Utilizador",
      email: user.email,
      avatar: "",
      role: "login-not-completed" as UserRole,
      createdAt: new Date().toISOString(),
      ...signupInfluencerCreateFields(),
    };
    await setDoc(userDocRef, newUser);
  }
}

const LoginForm: React.FC<LoginFormProps> = ({
  embedded = false,
  title,
  description,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<RegisterRole | null>(null); 
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('login');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, user: authUser } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('cadastro') === '1') {
      setFormMode('register');
      // Começa sem nenhuma opção marcada: a pessoa escolhe Paciente ou Terapeuta.
    } else {
      setFormMode('login');
    }
  }, [location.search]);

  // Retorno do login Google via redirect (fallback quando o popup é bloqueado no PC).
  useEffect(() => {
    let cancelled = false;

    const finishGoogleRedirect = async () => {
      const pending = sessionStorage.getItem(GOOGLE_REDIRECT_FLAG);
      try {
        const result = await getRedirectResult(auth);
        if (cancelled) {
          return;
        }
        if (!result?.user) {
          if (pending) {
            sessionStorage.removeItem(GOOGLE_REDIRECT_FLAG);
          }
          return;
        }

        setIsLoading(true);
        setError("");
        await ensureGoogleUserDocument(result.user);
        sessionStorage.removeItem(GOOGLE_REDIRECT_FLAG);
        // O useEffect de auth cuida do redirecionamento pós-login.
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        console.error("Erro no retorno do login Google (redirect):", err);
        sessionStorage.removeItem(GOOGLE_REDIRECT_FLAG);
        setError(
          "Não foi possível completar o login com o Google. Tente novamente ou use e-mail e senha.",
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void finishGoogleRedirect();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const checkUserStatusAndRedirect = async () => {
      if (!authLoading && isAuthenticated && authUser && !error) {
        console.log('Utilizador já autenticado detectado, verificando status...');
        
        try {
          const userDocRef = doc(db, 'users', authUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data() as { role: UserRole; status?: TherapistStatus };
            
            // Verificar se o usuário está suspenso
            if (userData.status === TherapistStatus.SUSPENDED) {
              console.log('Utilizador suspenso detectado, efetuando logout...');
              await auth.signOut();
              setError('Sua conta foi suspensa. Entre em contato com o administrador para mais informações.');
              return;
            }
            
            handleRedirect(userData.role);
          } else {
            handleRedirect('login-not-completed');
          }
        } catch (err) {
          console.error('Erro ao verificar status do usuário:', err);
          setError('Erro ao verificar status da conta. Tente novamente.');
        }
      }
    };
    
    checkUserStatusAndRedirect();
  }, [isAuthenticated, authLoading, authUser, error, navigate]);

  const handleRedirect = (userRole?: UserRole) => {
    console.log('Executando redirecionamento para usuário com role:', userRole);

    const planId = location.state?.planId;
    const from = location.state?.from;
    const currentPage = { pathname: location.pathname, search: location.search };
    const originRoute = from || (embedded ? currentPage : null);

    // Fallback cross-domain: ?redirect=/agendamento?... (paths internos apenas)
    const redirectParam = new URLSearchParams(location.search).get("redirect");
    const safeRedirect =
      redirectParam &&
      redirectParam.startsWith("/") &&
      !redirectParam.startsWith("//") &&
      !redirectParam.includes("://")
        ? redirectParam
        : null;

    if (userRole === 'login-not-completed') {
      console.log('Redirecionando para completar perfil');
      navigate('/completar-perfil', {
        replace: true,
        state: {
          ...(originRoute ? { from: originRoute } : {}),
          ...(planId ? { planId } : {}),
        },
      });
      return;
    }

    // Se veio de uma página específica (como checkout), redireciona de volta
    if (originRoute) {
      // Se a URL original já tem planId na query string, apenas redireciona
      if (originRoute.search && originRoute.search.includes('planId=')) {
        navigate(originRoute.pathname + originRoute.search, { replace: true });
        return;
      }

      // Se tem planId no state mas não na URL, adiciona ao state para ser processado
      if (planId) {
        navigate(originRoute.pathname, {
          replace: true,
          state: {
            planIdToPurchase: planId
          }
        });
        return;
      }

      // Caso contrário, apenas redireciona para a página original
      navigate(originRoute.pathname + (originRoute.search || ''), { replace: true });
      return;
    }

    if (safeRedirect) {
      navigate(safeRedirect, { replace: true });
      return;
    }

    // Apos login sem rota de origem, vai direto para o painel.
    // Antes ia para "/" (Home antiga), removida do app.
    navigate(POST_LOGIN_ROUTE, { replace: true });
  };

  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // O useEffect vai cuidar da verificação de status e redirecionamento
    } catch (err: any) {
      setError('Credenciais inválidas. Verifique seu e-mail e senha.');
    }
  };

  const showPostRegisterTutorialNotice = async (registeredRole: RegisterRole) => {
    const isTherapist = registeredRole === 'terapeuta';
    const html = isTherapist
      ? 'Enviamos para o seu e-mail o guia de acesso à sala virtual — confira sua caixa de entrada (e também o spam).<br/><br/>Você também pode assistir aos tutoriais quando quiser aqui no site, em <strong>Tutoriais</strong>.'
      : 'Você pode assistir aos tutoriais da plataforma quando quiser aqui no site, em <strong>Tutoriais</strong>.';

    const result = await Swal.fire({
      icon: 'success',
      title: 'Cadastro concluído!',
      html,
      showCancelButton: true,
      confirmButtonText: 'Ver tutoriais',
      cancelButtonText: 'Continuar',
      confirmButtonColor: '#2B5A41',
      cancelButtonColor: '#9CA3AF',
      allowOutsideClick: false,
    });

    if (result.isConfirmed) {
      navigate('/tutoriais');
    }
  };

  const handleRegister = async () => {
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    if (!role) {
      setError('Por favor, selecione se você é Paciente ou Terapeuta.');
      return;
    }
    if (!isValidBrazilPhone(phone)) {
      setError('Informe um telefone válido com DDD, ex.: (11) 94729-3221.');
      return;
    }
    try {
      const formattedPhone = formatBrazilPhone(phone);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const newUser = {
        uid: user.uid,
        name: name.trim(),
        email: user.email,
        phone: formattedPhone,
        role: role,
        createdAt: new Date().toISOString(),
        avatar: '',
        ...signupInfluencerCreateFields(),
      };
      // Cria o documento na coleção 'users'
      await setDoc(doc(db, 'users', user.uid), newUser);

      // --- MODIFICAÇÃO ADICIONADA AQUI ---
      // Se o novo utilizador for um terapeuta, cria também um documento na coleção 'therapists'
      if (role === 'terapeuta') {
        console.log(`Novo utilizador é um terapeuta. A criar documento em 'therapists' para UID: ${user.uid}`);
        const therapistRef = doc(db, 'therapists', user.uid);
        await setDoc(therapistRef, {
          userId: user.uid,
          nomePublico: name.trim(),
          email: email,
          phone: formattedPhone,
          horasDisponiveis: 0,
          horariosDisponiveis: {}, // Objeto vazio como valor inicial
          createdAt: serverTimestamp() // Usa o timestamp do servidor para o registo
        });
        console.log('Documento do terapeuta criado com sucesso.');
        await ensureTherapistWelcomeTrial();
        await ensureInfluencerSignupBonus();
      }
      // --- FIM DA MODIFICAÇÃO ---

      // Avisa o novo usuário sobre o tutorial (por e-mail, para terapeutas) e a
      // página de tutoriais do site. O redirect automático (useEffect) continua
      // acontecendo por baixo; o modal do SweetAlert fica por cima.
      await showPostRegisterTutorialNotice(role as RegisterRole);

      // Não chama handleRedirect diretamente: o useEffect que observa
      // isAuthenticated/authUser cuida do redirect de forma consistente com o login.
    } catch (err: any) {
        if (err.code === 'auth/email-already-in-use') {
            setError('Este e-mail já está em uso.');
        } else {
            setError('Erro ao criar conta. Tente novamente.');
        }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    if (formMode === 'login') {
      await handleLogin();
    } else {
      await handleRegister();
    }
    setIsLoading(false);
  };

  const toggleFormMode = () => {
    setFormMode((prev) => (prev === 'login' ? 'register' : 'login'));
    setError('');
    setPhone('');
  };

  const startGoogleRedirectFallback = async () => {
    // Desktop: popup bloqueado por navegador/extensão → redirect (mais confiável).
    sessionStorage.setItem(GOOGLE_REDIRECT_FLAG, "1");
    setError("Abrindo login do Google em tela cheia…");
    await signInWithRedirect(auth, googleProvider);
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Iniciando login com Google via popup...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('Login com Google bem-sucedido, verificando utilizador...');
      await ensureGoogleUserDocument(user);
      
      // O useEffect vai cuidar da verificação de status e redirecionamento
    } catch (error: any) {
      console.error("Erro durante login com Google:", error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Login cancelado pelo utilizador.");
      } else if (
        error.code === 'auth/popup-blocked' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        try {
          await startGoogleRedirectFallback();
          return;
        } catch (redirectErr) {
          console.error("Fallback redirect Google falhou:", redirectErr);
          setError(
            "O navegador bloqueou o login do Google. Permita pop-ups ou tente com e-mail e senha.",
          );
        }
      } else {
        setError("Não foi possível completar o login com o Google. Tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || authLoading || (!authLoading && isAuthenticated)) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-screen bg-[#E0EDE3]'}`}>
        <div className="flex items-center text-gray-600">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2B5A41]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Aguardando...
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'font-sans' : 'min-h-screen bg-gradient-to-br from-[#E0EDE3] to-[#DDEBF1] flex items-center justify-center p-4 font-sans'}`}>
      <div className={`w-full bg-white/80 backdrop-blur-lg rounded-3xl shadow-lg p-8 ${embedded ? 'border border-gray-100' : 'max-w-sm'}`}>
        <div className="text-center mb-8">
          {!embedded && (
            <>
              <a href={MARKETING_HOME_URL} className="inline-block">
                <img src="/assets/logo.png" alt="Logo" className="h-16 w-16 mx-auto mb-4 hover:opacity-80 transition-opacity" />
              </a>
              <h1 className="text-xl font-medium text-gray-700">Bem vindo ao</h1>
              <a href={MARKETING_HOME_URL} className="inline-block">
                <img src="/assets/constela.png" alt="Constela Brasil" className="h-10 w-auto mx-auto mt-1 hover:opacity-80 transition-opacity" />
              </a>
            </>
          )}
          {embedded && (
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-gray-800">{title || 'Entre para continuar'}</h1>
              <p className="text-sm text-gray-600">{description || 'Faça login ou crie sua conta para concluir a compra sem sair desta página.'}</p>
            </div>
          )}
        </div>

        {/* Botão Google no topo (layout aprovado pela cliente) */}
        <div className="mb-6">
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            type="button"
            className="w-full flex justify-center items-center gap-3 py-3 px-4 border border-gray-300 rounded-full shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-all"
          >
            <svg className="w-5 h-5" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
              <path fill="currentColor" d="M488 261.8C488 403.3 381.5 512 244 512 110.3 512 0 398.8 0 256S110.3 0 244 0c73 0 136.3 29.3 181.4 76.6L363.8 150.2C328.7 117.8 291.1 98.4 244 98.4c-85.6 0-154.4 68.8-154.4 157.6s68.8 157.6 154.4 157.6c93.1 0 125.3-56.2 129.1-86.3H244v-64.8h236.1c2.3 12.7 3.9 26.9 3.9 41.4z"></path>
            </svg>
            {formMode === 'login' ? 'Entrar com Google' : 'Cadastrar com Google'}
          </button>
          <div className="relative mt-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">ou continue com email</span></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className={embedded && formMode === 'register' ? 'grid grid-cols-1 sm:grid-cols-2 gap-5' : 'contents'}>
            {formMode === 'register' && (
              <FormInput id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" required>
                <User size={20} />
              </FormInput>
            )}
            {formMode === 'register' && (
              <FormInput
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatBrazilPhone(e.target.value))}
                placeholder="Telefone (WhatsApp)"
                required
              >
                <Phone size={20} />
              </FormInput>
            )}
            <FormInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Seu email" required>
              <Mail size={20} />
            </FormInput>
            <FormInput id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" required>
              <Lock size={20} />
            </FormInput>
            {formMode === 'register' && (
              <FormInput id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirme sua senha" required>
                <Lock size={20} />
              </FormInput>
            )}
          </div>
          {formMode === 'register' && (
            <div>
              <span className="text-sm font-medium text-gray-600 block mb-2">Eu sou:</span>
              <div className="grid grid-cols-2 gap-2">
                {(['paciente', 'terapeuta'] as RegisterRole[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`px-3 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                      role === r ? 'bg-[#2B5A41] text-white shadow-md scale-105' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {error && <p className="text-xs text-red-600 text-center pt-2">{error}</p>}
          <button
            type={embedded ? 'button' : 'submit'}
            onClick={embedded ? (e: React.MouseEvent<HTMLButtonElement>) => handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>) : undefined}
            disabled={isLoading}
            className="w-full bg-[#2B5A41] text-white font-semibold py-3 px-4 rounded-full hover:bg-[#203C2E] disabled:bg-opacity-70 transition-all transform hover:scale-105 flex items-center justify-center shadow-lg"
          >
            {isLoading && (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isLoading ? 'Processando...' : (formMode === 'login' ? 'Fazer Login' : 'Criar Conta')}
          </button>

          <button
            type="button"
            onClick={toggleFormMode}
            disabled={isLoading}
            className="w-full mt-3 py-3 px-4 rounded-full border-2 border-[#2B5A41] bg-white text-[#2B5A41] font-semibold hover:bg-[#E0EDE3] disabled:opacity-60 transition-all"
          >
            {formMode === 'login' ? 'Cadastre-se gratuitamente' : 'Já tenho conta. Fazer login'}
          </button>

          {/* Link "Esqueci minha senha" - só aparece no modo login */}
          {formMode === 'login' && (
            <div className="mt-3 text-center">
              <Link
                to="/esqueci-senha"
                className="text-sm text-[#2B5A41] hover:text-[#203C2E] font-medium hover:underline"
              >
                Esqueci minha senha
              </Link>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
